import React, { useCallback, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { fetchServices } from './api';
import { FilterChips } from './FilterChips';
import { formatCount, formatMs, formatPct } from './formatHealth';
import { HealthError, HealthPageHeader, HealthSkeleton } from './HealthPageHeader';
import { StatusPill } from './StatusBits';
import type { TimeWindow } from './types';
import { useLivePoll } from './useLivePoll';

const WINDOW_OPTIONS: { id: TimeWindow; label: string }[] = [
  { id: '1h', label: '1h' },
  { id: '6h', label: '6h' },
  { id: '24h', label: '24h' },
  { id: '7d', label: '7d' },
];

export const ServicesHealthOverlay: React.FC = () => {
  const navigate = useNavigate();
  const [windowKey, setWindowKey] = useState<TimeWindow>('1h');

  const onUnauth = useCallback(() => {
    sessionStorage.removeItem('portal_token');
    sessionStorage.removeItem('portal_user');
    navigate('/admin/login', { replace: true });
  }, [navigate]);

  const fetcher = useCallback((signal: AbortSignal) => fetchServices(windowKey, signal), [windowKey]);
  const { data, loading, refreshing, error, reload } = useLivePoll({
    intervalMs: 15_000,
    fetcher,
    onUnauthenticated: onUnauth,
    resetKey: windowKey,
  });

  if (loading && !data) return <HealthSkeleton />;
  if (error && !data) return <HealthError message={error} onRetry={() => reload()} />;
  if (!data) return null;

  return (
    <div className="space-y-6">
      <Link
        to="/admin/system"
        className="inline-flex items-center gap-1.5 text-sm font-medium text-[var(--mymoolah-blue)] hover:underline"
      >
        <ArrowLeft className="h-4 w-4" aria-hidden />
        Platform Health
      </Link>

      <HealthPageHeader
        title="Services"
        subtitle="Cloud Run CPU, instances, latency, and the public API health probe."
        status={data.overall.status}
        reason={data.overall.reason}
        environment={data.environment}
        region="africa-south1"
        generatedAt={data.generatedAt}
        live={!document.hidden}
        refreshing={refreshing}
        onRefresh={() => reload(true)}
      />

      <FilterChips label="Window" value={windowKey} options={WINDOW_OPTIONS} onChange={setWindowKey} />

      <p className="text-xs text-[var(--muted-foreground)]">
        API probe: <span className="font-mono">{data.healthProbeUrl}</span>
      </p>

      <div className="overflow-hidden rounded-xl border border-[var(--border)] bg-[var(--card)] shadow-[var(--portal-shadow)]">
        <table className="min-w-full text-left text-sm">
          <thead className="border-b border-[var(--border)] bg-[var(--muted)]/50 text-xs uppercase tracking-wider text-[var(--muted-foreground)]">
            <tr>
              <th className="px-5 py-3 font-semibold">Service</th>
              <th className="px-5 py-3 font-semibold">Status</th>
              <th className="px-5 py-3 font-semibold">Instances</th>
              <th className="px-5 py-3 font-semibold">CPU</th>
              <th className="px-5 py-3 font-semibold">Memory</th>
              <th className="px-5 py-3 font-semibold">p95</th>
              <th className="px-5 py-3 font-semibold">5xx</th>
              <th className="px-5 py-3 font-semibold">Probe</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[var(--border)]">
            {data.services.map((svc) => (
              <tr key={svc.id}>
                <td className="px-5 py-4">
                  <p className="font-semibold text-[var(--foreground)]">{svc.label}</p>
                  <p className="font-mono text-[11px] text-[var(--muted-foreground)]">{svc.cloudRunName}</p>
                </td>
                <td className="px-5 py-4">
                  <StatusPill status={svc.status} />
                </td>
                <td className="px-5 py-4 font-mono tabular-nums">{formatCount(svc.instances)}</td>
                <td className="px-5 py-4 font-mono tabular-nums">{formatPct(svc.cpuPercent)}</td>
                <td className="px-5 py-4 font-mono tabular-nums">{formatPct(svc.memoryPercent)}</td>
                <td className="px-5 py-4 font-mono tabular-nums">{formatMs(svc.p95Ms)}</td>
                <td className="px-5 py-4 font-mono tabular-nums">{formatPct(svc.errorRatePercent, 2)}</td>
                <td className="px-5 py-4 text-xs text-[var(--muted-foreground)]">
                  {svc.healthProbe?.ok == null
                    ? '—'
                    : svc.healthProbe.ok
                      ? `OK ${formatMs(svc.healthProbe.latencyMs)}`
                      : svc.healthProbe.error || `HTTP ${svc.healthProbe.statusCode ?? '—'}`}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
